import {
    EventEmitter,
    inject,
    Injectable,
    ViewContainerRef,
} from '@angular/core';
import { ofType } from '@ngrx/effects';
import { Action, ActionsSubject } from '@ngrx/store';
import { LookupFieldResult, SystemEntity } from '@wdx/clmi/clmi-swagger-gen';
import { NavItem } from '@wdx/shared/utils';
import {
    Observable,
    of,
    Subject,
    delay,
    filter,
    map,
    take,
    takeUntil,
} from 'rxjs';
import { MoleculeInputLookupComponent } from '../../../../components/molecules/molecule-input-lookup/containers/molecule-input-lookup/molecule-input-lookup.component';
import {
    ASSIGN_TO_LOOKUP_DATA,
    ASSIGN_TO_LOOKUP_DATA_USER_TEAM,
    ASSIGN_TO_USER_LOOKUP_DATA,
} from '../../../../constants/assign.constants';
import { ASSIGN_TO_MENU_ITEM } from '../../../../constants/menu.constants';
import { ModalManagementService } from '@wdx/clmi/ui';
import { AssignFacadeService } from '../../../../state/assign/assign-facade.service';
import * as assignActions from '../../../../state/assign/assign.actions';
import { SystemEventsFacade } from '../../../../state/system-events/system-events-facade.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
    providedIn: 'root',
})
export class AssignToService {
    private systemEventsFacade = inject(SystemEventsFacade);
    private assignFacadeService = inject(AssignFacadeService);
    private actionsSubject$ = inject(ActionsSubject);
    private modalManagementService = inject(ModalManagementService);

    /**
     * The reference to the lookup container. Note this is initialised in base AppRoute component.
     */
    assignLookupContainer: ViewContainerRef;
    assignee$ = new Subject<LookupFieldResult>();
    isContextual = false;
    isTeam = false;

    /**
     * Returns 'Assign to' menu item, with actions included
     * @param entityType
     * @param entityId
     */
    getAssignToMenuItem(entityType: SystemEntity, entityId: string): NavItem {
        return {
            ...ASSIGN_TO_MENU_ITEM,
            cySelector: `option-assignTo-${entityId}`,
            callback: () => this.openAssignToModal(entityType, entityId),
        };
    }

    /**
     * Opens the 'Assign to' lookup modal
     * - dynamically creates and injects lookup component into container reference
     * - opens 'Assign to' lookup modal
     * - dispatches assignTo action on success and
     * - cleans container on success and close
     * - isContextual - if a entity type and entity id is available then it should use the LookupSourceType.Assignee as the lookup source
     * - isTeam - if a entity type is SystemEntity.Team then it should use the LookupSourceType.User as the lookup source
     * @param entityType
     * @param entityId - optional, provide for a single entity to be assigned, do not provide if bulkEntityIds is providded
     * @param bulkEntityIds  - optional, assign multiple entities, overrides entityId value
     */
    openAssignToModal(
        entityType: SystemEntity,
        entityId?: string,
        bulkEntityIds?: string[],
    ): EventEmitter<void> {
        let bulkMode = false;
        if (bulkEntityIds && bulkEntityIds.length) {
            entityId = bulkEntityIds[0];
            bulkMode = true;
        }
        this.isContextual = Boolean(entityType && entityId);
        this.isTeam = Boolean(entityType === SystemEntity.Team);
        const lookupComponent = this.assignLookupContainer.createComponent(
            MoleculeInputLookupComponent,
        );
        const lookupInstance = lookupComponent.instance;

        const formInputData = this.isContextual
            ? this.isTeam
                ? ASSIGN_TO_USER_LOOKUP_DATA
                : ASSIGN_TO_LOOKUP_DATA
            : ASSIGN_TO_LOOKUP_DATA_USER_TEAM;

        lookupInstance.showReadout = false;
        lookupInstance.formInputData = formInputData;
        lookupInstance.formInputData.entityType = entityType;
        lookupInstance.formInputData.entityId = entityId;
        lookupInstance.initialValue = '';
        lookupInstance.showNoResultsMessage = true;
        let modalRef: NgbModalRef;

        lookupInstance.modalRegistered.pipe(delay(0)).subscribe((modalId) => {
            modalRef = this.modalManagementService.openModalWithId(modalId);
            if (this.isContextual && !this.isTeam) {
                lookupInstance.dispatchGetLook({
                    searchText: '',
                    lookupSourceDefinition: formInputData.lookupSources[0],
                });
            }
        });

        lookupInstance.valueChanged.pipe(delay(0)).subscribe((value) => {
            if (entityType && entityId) {
                if (bulkMode) {
                    this.assignFacadeService.bulkAssignToEvent({
                        entityType,
                        entityIds: bulkEntityIds,
                        partyId: value.id,
                    });
                } else {
                    this.assignFacadeService.assignToEvent({
                        entityType,
                        entityId,
                        partyId: value.id,
                    });
                }
            } else {
                this.assignee$.next(value);
            }

            this.assignLookupContainer.clear();
        });

        lookupInstance.closed.pipe(delay(0)).subscribe(() => {
            this.assignLookupContainer.clear();
        });

        return lookupInstance.closed;
    }

    /**
     * @deprecated listens to actions directly which is not desirable, also it does not handle the error case
     * and would result in a memory leak in that scenario due to no cleanup - use assignToComplete$() instead
     */
    onAssignToSuccess$(entityType?: SystemEntity): Observable<Action> {
        return this.actionsSubject$.pipe(
            ofType(assignActions.assignToSuccess),
            filter((action) =>
                entityType ? action.entityType === entityType : true,
            ),
        );
    }

    assignToComplete$(
        entityType: SystemEntity,
        entityId: string,
        autoCleanupDelay = 2000,
    ): Observable<{
        entityType: SystemEntity;
        entityId: string;
        partyId: string;
    }> {
        return this.systemEventsFacade
            .getEventsOfSourceActionTypes$<{
                entityType: SystemEntity;
                entityId: string;
                partyId: string;
            }>([
                assignActions.assignToSuccess.type,
                assignActions.assignToFailure.type,
            ])
            .pipe(
                takeUntil(of(null).pipe(delay(autoCleanupDelay))),
                filter(
                    (action) =>
                        action.sourceAction.entityType === entityType &&
                        action.sourceAction.entityId === entityId,
                ),
                map(({ sourceAction }) => sourceAction),
                take(1),
            );
    }
}
